import {
  faCode,
  faContactBook,
  faFileUser,
  faHome,
  faMapMarkerAlt,
  faMobile,
  faNewspaper,
  faRectangleList,
  faStore,
  faTachometer,
  faUserCrown,
} from '@fortawesome/pro-light-svg-icons'

import { generateApplicationUrl } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'

import { systemManagerIcons } from '@functions/navigation'

import type { UseEntityImpersonationPayload } from '@hooks/useEntityImpersonation'

type GenerateMainNavigationItemsProps = {
  callbacks: {
    linkToTemplates: Function,
    unlinkImpersonation?: UseEntityImpersonationPayload['callbacks']['unlinkImpersonation'],
  },
  currentUserPayload: ReturnType<typeof useCurrentUser>,
}

const generateMainNavigationItems = (props: GenerateMainNavigationItemsProps) => {
  const {
    callbacks: {
      linkToTemplates,
      unlinkImpersonation,
    },
    currentUserPayload,
  } = props

  const {
    brand,
    impersonatedUser,
    impersonatingUser,
    isAdmin,
    isBrandUser,
    isImpersonatingUser,
    isMasterAdmin,
    isProviderDirector,
    isProviderUser,
    provider,
    provider: { is_publication_provider },
  } = currentUserPayload

  // @ts-expect-error TS2339
  const { applicationUrl: templateManagerUrl } = generateApplicationUrl({
    applicationKey: 'template_manager',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  return ({
    sections: [
      {
        key: 'dashboard',
        visible: isAdmin || isBrandUser || isProviderDirector,
        items: [
          {
            href: `#/systemManager/providers/${provider?.id}/edit`,
            icon: faFileUser,
            key: 'providers',
            title: 'Account',
            visible: isProviderDirector,
          },
          {
            href: '#/',
            icon: faTachometer,
            key: 'help-desk',
            title: 'Help Desk',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/newsDesk/bookings',
            icon: faNewspaper,
            key: 'news-desk',
            title: 'News Desk',
            visible: isAdmin || is_publication_provider,
          },
        ],
      },
      {
        key: 'primary',
        title: 'Primary',
        visible: isAdmin || isBrandUser || isProviderDirector || isProviderUser,
        items: [
          {
            href: '#/systemManager/agencies',
            icon: systemManagerIcons.agencies,
            key: 'agencies',
            title: 'Agencies',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/systemManager/brands',
            icon: systemManagerIcons.brands,
            key: 'brands',
            title: 'Brands',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/providerAgencies',
            icon: faContactBook,
            key: 'provider-accounts',
            title: 'Agencies',
            visible: isProviderDirector,
          },
          {
            href: '#/systemManager/invoices',
            icon: systemManagerIcons.invoices,
            key: 'invoices',
            title: 'Invoices',
            visible: isAdmin || isProviderDirector,
          },
          {
            href: '#/systemManager/orders',
            icon: faStore,
            key: 'orders',
            title: 'Orders',
            visible: isBrandUser || isProviderUser,
          },
          {
            href: '#/systemManager/providerPackages',
            icon: systemManagerIcons.packages,
            key: 'packages',
            title: 'Packages',
            visible: isAdmin || isProviderDirector,
          },
          {
            href: '#/systemManager/providerPriceLists',
            icon: systemManagerIcons['price-lists'],
            key: 'price-lists',
            title: 'Price Lists',
            visible: isAdmin || isProviderDirector,
          },
          {
            href: '#/systemManager/products',
            icon: systemManagerIcons.products,
            key: 'products',
            title: 'Products',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/providers',
            icon: systemManagerIcons.providers,
            key: 'providers',
            title: 'Providers',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/publications',
            icon: systemManagerIcons.publications,
            key: 'publications',
            title: 'Publications',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/tasks',
            icon: faRectangleList,
            key: 'tasks',
            title: 'Tasks',
            visible: isProviderDirector,
          },
          {
            href: isBrandUser ? `${templateManagerUrl}/#/templatePages?hidden=false` : undefined,
            onClick: isBrandUser ? undefined : () => linkToTemplates(),
            icon: systemManagerIcons.templates,
            key: 'templates',
            title: 'Templates',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/systemManager/users',
            icon: systemManagerIcons.users,
            key: 'users',
            title: 'Users',
            visible: isAdmin || isBrandUser || isProviderDirector,
          },
        ],
      },
      {
        key: 'additional',
        title: 'Additional',
        visible: isAdmin || isBrandUser || isProviderUser,
        items: [
          {
            href: '#/systemManager/brandGroups',
            icon: systemManagerIcons['brand-groups'],
            key: 'brand-groups',
            title: 'Brand Groups',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/systemManager/brandThemes',
            icon: systemManagerIcons['brand-themes'],
            key: 'brand-themes',
            title: 'Brand Themes',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/orderBundles',
            icon: systemManagerIcons.bundles,
            key: 'bundles',
            title: 'Bundles',
            visible: isAdmin || isProviderDirector,
          },
          {
            href: '#/systemManager/campaigns',
            icon: systemManagerIcons.campaigns,
            key: 'campaigns',
            title: 'Campaigns',
            visible: isAdmin || isProviderUser,
          },
          {
            href: '#/systemManager/providerProductCodes',
            icon: systemManagerIcons['product-codes'],
            key: 'product-codes',
            title: 'Product Codes',
            visible: isProviderUser,
          },
          {
            href: '#/systemManager/notifications',
            icon: systemManagerIcons.notifications,
            key: 'notifications',
            title: 'Notifications',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/paginationRules',
            icon: systemManagerIcons['pagination-rules'],
            key: 'pagination-rules',
            title: 'Pagination Rules',
            visible: isAdmin,
          },
        ],
      },
      {
        key: 'admin',
        title: 'Admin',
        visible: isBrandUser,
        items: [
          {
            href: `#/systemManager/brands/${brand?.id}/edit/`,
            icon: systemManagerIcons.brands,
            key: 'brand-admin',
            title: 'Brand Admin',
            visible: isBrandUser,
          },
        ],
      },
      {
        key: 'advanced',
        title: 'Advanced',
        visible: isAdmin || isBrandUser || isProviderUser,
        items: [
          {
            href: '#/systemManager/geoCountries',
            icon: systemManagerIcons.countries,
            key: 'countries',
            title: 'Countries',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/courses',
            icon: systemManagerIcons['training-courses'],
            key: 'training-courses',
            title: 'Courses',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/fonts',
            icon: systemManagerIcons.fonts,
            key: 'fonts',
            title: 'Fonts',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/imageLibraries',
            icon: systemManagerIcons.libraries,
            key: 'libraries',
            title: 'Libraries',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/systemManager/paymentMethods',
            icon: systemManagerIcons['payment-methods'],
            key: 'payment-methods',
            title: 'Payment Methods',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/portals',
            icon: systemManagerIcons.portals,
            key: 'portals',
            title: 'Portals',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/pricingPlans',
            icon: systemManagerIcons['pricing-plans'],
            key: 'pricing-plans',
            title: 'Pricing Plans',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/processingFees',
            icon: systemManagerIcons['processing-fees'],
            key: 'processing-fees',
            title: 'Processing Fees',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/reporting',
            icon: systemManagerIcons.reporting,
            key: 'reporting',
            title: 'Reporting',
            visible: isAdmin || isBrandUser || isProviderUser,
          },
          {
            href: '#/systemManager/roles',
            icon: systemManagerIcons.roles,
            key: 'roles',
            title: 'Roles',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/serviceCategories',
            icon: systemManagerIcons['service-categories'],
            key: 'service-categories',
            title: 'Service Categories',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/services',
            icon: systemManagerIcons.services,
            key: 'services',
            title: 'Services',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/statuses',
            icon: systemManagerIcons.statuses,
            key: 'statuses',
            title: 'Statuses',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/geoSuburbs',
            icon: systemManagerIcons.suburbs,
            key: 'suburbs',
            title: 'Suburbs',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/systems',
            icon: systemManagerIcons.systems,
            key: 'systems',
            title: 'Systems',
            visible: isMasterAdmin,
          },
          {
            href: '#/systemManager/teams',
            icon: systemManagerIcons.teams,
            key: 'teams',
            title: 'Teams',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/tags',
            icon: systemManagerIcons.tags,
            key: 'tags',
            title: 'Tags',
            visible: isAdmin || isBrandUser,
          },
          {
            href: '#/systemManager/templateCategories',
            icon: systemManagerIcons['template-categories'],
            key: 'template-categories',
            title: 'Template Categories',
            visible: isAdmin || isBrandUser,
          },
        ],
      },
      {
        key: 'logs',
        title: 'Logs',
        visible: isAdmin,
        items: [
          {
            href: '#/systemManager/apiLogs',
            icon: faCode,
            key: 'api-logs',
            title: 'API Logs',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/loginSessions',
            icon: faMapMarkerAlt,
            key: 'login-sessions',
            title: 'Login Sessions',
            visible: isAdmin,
          },
          {
            href: '#/systemManager/smsLogs',
            icon: faMobile,
            key: 'sms-logs',
            title: 'SMS Logs',
            visible: isAdmin,
          },
        ],
      },
      {
        key: 'impersonation',
        title: 'Impersonating',
        visible: isImpersonatingUser,
        items: [
          {
            icon: faUserCrown,
            key: 'user',
            title: impersonatedUser.full_name,
            visible: true,
          },
          {
            icon: faHome,
            key: 'back-to-admin',
            // @ts-expect-error TS2322
            onClick: unlinkImpersonation ? () => unlinkImpersonation({ userId: impersonatingUser?.id }) : undefined,
            title: 'Back to Admin',
            visible: true,
          },
        ],
      },
    ],
  })
}

export default generateMainNavigationItems
